import {CartItem, CartItemType} from '@/_application/modules/cart/models/cart';
import {
    CartItemInterface,
    CartItemPrices,
    ConfigurableCartItem,
    ConfigurableProduct,
    ConfigurableVariant,
} from '../../../__generated__/__types__';

export interface UpdateItemInput extends Partial<Omit<CartItemInterface, 'id'>> {}

export function addItemWithQuantity(items: CartItem[], item: CartItemInterface, quantity: number) {
    if (quantity <= 0) throw new Error("cartQuantity can't be zero or less than zero");
    const existingItemIndex = items.findIndex(currentItem => {
        if (currentItem.__typename === CartItemType.CONFIGURABLE) {
            const isExisting = isConfigurableCartItemExisting(currentItem, item);
            return isExisting;
        }
        return currentItem.product.id === item.product.id;
    });

    if (existingItemIndex > -1) {
        const newItems = [...items];
        const currentItem = newItems[existingItemIndex];
        const newQuantity = currentItem.quantity + quantity;
        const updatedCartItem = updateCartItemQuantityAndPrices(currentItem, newQuantity);
        newItems[existingItemIndex] = updatedCartItem;
        return newItems;
    }
    const newCartItem = updateCartItemQuantityAndPrices({...item}, quantity);
    return [...items, newCartItem];
}

export function removeItemOrQuantity(items: CartItemInterface[], id: CartItemInterface['id'], quantity: number) {
    return items.reduce((acc: CartItemInterface[], item) => {
        if (item.id === id) {
            const newQuantity = item.quantity - quantity;
            const updatedCartItem = updateCartItemQuantityAndPrices(item, newQuantity);
            return newQuantity > 0 ? [...acc, {...updatedCartItem}] : [...acc];
        }
        return [...acc, item];
    }, []);
}

export function updateItemWithQuantity(items: CartItemInterface[], id: CartItemInterface['id'], quantity: number) {
    const existingItemIndex = items.findIndex(item => item.id === id);
    if (existingItemIndex === -1) {
        return items;
    }
    const newItems = [...items];
    const currentItem = newItems[existingItemIndex];
    const updatedCartItem = updateCartItemQuantityAndPrices(currentItem, quantity);
    newItems[existingItemIndex] = updatedCartItem;
    return newItems;
}
// Simple CRUD for Item
export function addItem(items: CartItemInterface[], item: CartItemInterface) {
    return [...items, item];
}

export function getItem(items: CartItemInterface[], id: CartItemInterface['id']) {
    if (!items) return undefined;
    return items.find(item => item?.id === id);
}

export function updateItem(items: CartItemInterface[], id: CartItemInterface['id'], item: UpdateItemInput) {
    return items.map(existingItem => (existingItem.id === id ? {...existingItem, ...item} : existingItem));
}

export function removeItem(items: CartItemInterface[], id: CartItemInterface['id']) {
    return items.filter(existingItem => existingItem.id !== id);
}
export function inStock(items: CartItemInterface[], id: CartItemInterface['id']) {
    const item = getItem(items, id);
    if (item) return item['quantity']! < 2; // @TODO: should fetch from backend
    return false;
}
export const calculateItemTotals = (items: CartItemInterface[]) =>
    items.map(item => ({
        ...item,
        itemTotal: item.prices?.row_total.value || 0,
    }));

export const calculateTotal = (items: CartItemInterface[]) =>
    items.reduce((total, item) => total + (item.prices?.row_total.value || 0), 0);

export const calculateTotalItems = (items: CartItemInterface[]) => items.reduce((sum, item) => sum + item.quantity!, 0);

export const calculateUniqueItems = (items: CartItemInterface[]) => items.length;

interface PriceValues {
    totalAmount: number;
    tax: number;
    shipping_charge: number;
}
export const calculatePaidTotal = ({totalAmount, tax, shipping_charge}: PriceValues, discount?: number) => {
    let paidTotal = totalAmount + tax + shipping_charge;
    if (discount) {
        paidTotal = paidTotal - discount;
    }
    return paidTotal;
};

export function getItemByProduct(items: CartItem[], cartItem: CartItemInterface) {
    if (!items) return undefined;

    return items.find(item => {
        if (item.__typename === CartItemType.CONFIGURABLE) {
            const isNewCartItem = item.id.startsWith('new-');
            const isExisting = isConfigurableCartItemExisting(item, cartItem);
            return isExisting || isNewCartItem;
        }
        return item?.product.id === cartItem.product.id;
    });
}

export function getCartItemVariation(item: ConfigurableCartItem): ConfigurableVariant | undefined {
    // get the value array of selected configurable options and filter null or undefined value
    const selectedConfigurableOptionValue = item.configurable_options
        .map(option => option?.value_id)
        .filter(value => !!value);
    const configurableProduct = item.product as ConfigurableProduct;
    if (!configurableProduct?.variants) {
        return undefined;
    }
    for (const variant of configurableProduct.variants) {
        if (!variant) {
            continue;
        }

        let matchOptionValueCount = 0;
        variant?.attributes?.forEach(attribute => {
            if (selectedConfigurableOptionValue.includes(attribute?.value_index!)) {
                matchOptionValueCount++;
            }
        });

        if (matchOptionValueCount === selectedConfigurableOptionValue.length) {
            return variant;
        }
    }
    return undefined;
}

export function isConfigurableCartItemExisting(item: ConfigurableCartItem, cartItem: CartItemInterface) {
    const matchVariation = getCartItemVariation(item);
    const isMatchVariation = matchVariation?.product?.id === cartItem.product.id;
    const isMatchCartItemId = item.id === cartItem.id;
    return isMatchVariation || isMatchCartItemId;
}

/**
 * Get the effective unit price for a cart item.
 * Uses prices.price.value (Magento unit price) as primary source.
 * Falls back to product price_range for catalog price.
 */
const getItemEffectiveUnitPrice = (item: CartItemInterface): number => {
    // Best: cart-level unit price (post catalog-rule / special price)
    if (item.prices?.price?.value != null && item.prices.price.value > 0) {
        return item.prices.price.value;
    }
    // Fallback: product final_price
    const finalPrice = item.product?.price_range?.minimum_price?.final_price?.value;
    if (finalPrice != null && finalPrice > 0) {
        return finalPrice;
    }
    // Last resort: regular price
    return item.product?.price_range?.minimum_price?.regular_price?.value ?? 0;
};

/**
 * Update cart item quantity and recalculate prices using unit-price derivation.
 * Strips stale item-level discounts — accurate discounts arrive via FastCartTotals.
 */
const updateCartItemQuantityAndPrices = (item: CartItemInterface, updatedQuantity: number): CartItemInterface => {
    const unitPrice = getItemEffectiveUnitPrice(item);
    const newRowTotal = unitPrice * updatedQuantity;

    const rowTotal = item?.prices?.row_total;
    const rowTotalIncludingTax = item?.prices?.row_total_including_tax;

    const updatedPrices: CartItemPrices = {
        ...(item?.prices as CartItemPrices),
        price: {
            ...(item?.prices?.price),
            value: unitPrice,
        },
        row_total: {
            ...rowTotal,
            value: newRowTotal,
        },
        row_total_including_tax: {
            ...rowTotalIncludingTax,
            value: newRowTotal,
        },
        discounts: null,
        total_item_discount: { __typename: 'Money', value: 0, currency: rowTotal?.currency },
    };

    return {
        ...item,
        quantity: updatedQuantity,
        prices: updatedPrices,
    };
};

